import { useEffect, useState, useCallback, useRef } from "react";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/Panel";
import { numTrabajo } from "@/pages/TrabajosActivos";
import { ShieldAlert, Loader2, RefreshCw, Lightbulb, UploadCloud } from "lucide-react";

const ESTADO_CLS = {
  pendiente: "bg-slate-100 text-slate-600", en_proceso: "bg-indigo-100 text-indigo-700",
  ok: "bg-emerald-100 text-emerald-700", problema: "bg-amber-100 text-amber-800",
  error: "bg-pink-100 text-pink-700",
};
const ESTADO_LABEL = {
  pendiente: "Pendiente", en_proceso: "En proceso", ok: "OK", problema: "Problema", error: "Error",
};

const hoyIso = () => new Date().toISOString().slice(0, 10);

const rangoLote = (l) => l.tipo === "diario" ? `Diario · ${l.fecha}` : `${l.desde} a ${l.hasta}`;

export default function ControlHonda() {
  const { isAdmin } = useAuth();
  const [desde, setDesde] = useState(hoyIso());
  const [hasta, setHasta] = useState(hoyIso());
  const [verificando, setVerificando] = useState(false);
  const [lotes, setLotes] = useState([]);
  const [cargandoLotes, setCargandoLotes] = useState(true);
  const [loteId, setLoteId] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [errorDetalle, setErrorDetalle] = useState("");
  const [usandoSugeridaId, setUsandoSugeridaId] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [cargandoPendientes, setCargandoPendientes] = useState(true);
  const [subiendoSintomas, setSubiendoSintomas] = useState(false);
  const inputSintomasRef = useRef(null);
  const intervaloRef = useRef(null);

  const cargarPendientes = useCallback(async () => {
    try {
      const { data } = await api.get("/honda-verificaciones/pendientes");
      setPendientes(data);
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setCargandoPendientes(false); }
  }, []);

  useEffect(() => { cargarPendientes(); }, [cargarPendientes]);

  const diasDesde = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

  const cargarLotes = useCallback(async () => {
    try {
      const { data } = await api.get("/honda-verificaciones/lotes");
      setLotes(data);
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setCargandoLotes(false); }
  }, []);

  useEffect(() => { cargarLotes(); }, [cargarLotes]);

  const cargarDetalle = useCallback(async () => {
    if (!loteId) return;
    try {
      const { data } = await api.get(`/honda-verificaciones/lotes/${loteId}`);
      setDetalle(data);
      setErrorDetalle("");
    } catch (err) { setErrorDetalle(errorMsg(err)); }
  }, [loteId]);

  useEffect(() => {
    setDetalle(null);
    setErrorDetalle("");
    if (loteId) cargarDetalle();
  }, [loteId, cargarDetalle]);

  useEffect(() => {
    if (!loteId || !detalle || detalle.terminado) { clearInterval(intervaloRef.current); return; }
    intervaloRef.current = setInterval(cargarDetalle, 5000);
    return () => clearInterval(intervaloRef.current);
  }, [loteId, detalle, cargarDetalle]);

  const verificar = async () => {
    if (!desde || !hasta) { toast.error("Seleccione el rango de fechas"); return; }
    setVerificando(true);
    try {
      const { data } = await api.post("/honda-verificaciones", { desde, hasta });
      toast.success(`Verificación iniciada: ${data.total} cupón(es) en el rango`);
      await cargarLotes();
      setLoteId(data.id);
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setVerificando(false); }
  };

  const usarSugerida = async (item) => {
    setUsandoSugeridaId(item.id);
    try {
      await api.post(`/honda-verificaciones/items/${item.id}/usar-sugerida`);
      toast.success(`Se usó la orden sugerida ${item.orden_sugerida} y se volvió a verificar`);
      await cargarLotes();
      await cargarDetalle();
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setUsandoSugeridaId(null); }
  };

  const subirSintomas = async (file) => {
    if (!file) return;
    setSubiendoSintomas(true);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      const { data } = await api.post("/honda-sintomas/importar", fd);
      toast.success(`Catálogo cargado: ${data.cantidad_sintomas} síntomas, ${data.cantidad_detalles} detalles, ${data.cantidad_finales} finales`);
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setSubiendoSintomas(false); }
  };

  const items = detalle?.items || [];
  const procesados = detalle ? items.length - (detalle.resumen.pendiente || 0) - (detalle.resumen.en_proceso || 0) : 0;

  return (
    <div data-testid="control-honda-page" className="space-y-4">
      <div className="flex items-center gap-2.5">
        <ShieldAlert size={20} className="text-indigo-600" />
        <h1 className="text-lg font-bold text-slate-800">Control de órdenes Honda</h1>
      </div>
      <p className="text-sm text-slate-500 -mt-2">
        El robot revisa, solo lectura, que el número de orden Honda de cada cupón finalizado aparezca correcto en el
        historial de Honda. Todos los días a las 6:00 p. m. se corre automáticamente una verificación de los cupones
        finalizados ese día; aquí también se puede pedir una verificación por rango de fechas.
      </p>

      {isAdmin && (
        <Panel titulo="Catálogo de síntomas Honda" testId="control-honda-sintomas-panel">
          <p className="text-xs text-slate-500 -mt-1 mb-2">
            Sube el JSON de síntomas exportado de Honda (texto/valor por síntoma, detalle y detalle final). Reemplaza
            el catálogo completo que usa el reporte de garantía.
          </p>
          <input ref={inputSintomasRef} type="file" accept="application/json,.json" className="hidden"
            data-testid="control-honda-sintomas-input"
            onChange={(e) => { subirSintomas(e.target.files?.[0]); e.target.value = ""; }} />
          <Button data-testid="control-honda-sintomas-subir-btn" variant="outline" disabled={subiendoSintomas}
            onClick={() => inputSintomasRef.current?.click()} className="gap-1.5">
            <UploadCloud size={14} />
            {subiendoSintomas ? "Cargando..." : "Cargar síntomas Honda (JSON)"}
          </Button>
        </Panel>
      )}

      <Panel titulo="Verificar por rango de fechas" testId="control-honda-rango-panel">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Desde</label>
            <Input data-testid="control-honda-desde-input" type="date" value={desde}
              onChange={(e) => setDesde(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Hasta</label>
            <Input data-testid="control-honda-hasta-input" type="date" value={hasta}
              onChange={(e) => setHasta(e.target.value)} className="w-40" />
          </div>
          <Button data-testid="control-honda-verificar-btn" onClick={verificar} disabled={verificando}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {verificando ? "Iniciando..." : "Verificar"}
          </Button>
        </div>
      </Panel>

      <Panel titulo="Cupones con orden Honda pendiente" testId="control-honda-pendientes-panel">
        <p className="text-xs text-slate-500 -mt-1 mb-2">
          Honda no dejó crear el cupón porque la moto tenía otra orden abierta (o el asesor lo marcó pendiente por
          otro motivo). El robot vuelve a intentarlo cada día a las 6:00 p. m.
        </p>
        {cargandoPendientes ? (
          <p className="text-sm text-slate-500">Cargando...</p>
        ) : pendientes.length === 0 ? (
          <p data-testid="control-honda-pendientes-vacio" className="text-sm text-slate-400">No hay cupones pendientes.</p>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="control-honda-pendientes-tabla" className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 pr-3 font-semibold">Trabajo</th>
                  <th className="py-2 pr-3 font-semibold">Vehículo</th>
                  <th className="py-2 pr-3 font-semibold">Motivo</th>
                  <th className="py-2 pr-3 font-semibold">Orden bloqueante</th>
                  <th className="py-2 font-semibold">Días pendiente</th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((p) => (
                  <tr key={p.trabajo_id} data-testid={`control-honda-pendiente-${p.trabajo_id}`} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-indigo-700">{numTrabajo(p.trabajo_numero)}</td>
                    <td className="py-2 pr-3 text-slate-700">
                      {p.vehiculo ? `${p.vehiculo.modelo} · ${p.vehiculo.placa}` : "—"}
                    </td>
                    <td className="py-2 pr-3 text-slate-600 text-xs max-w-xs">
                      {p.orden_honda_pendiente?.tipo === "otro"
                        ? p.orden_honda_pendiente.texto
                        : "La moto tiene otra orden abierta en Honda"}
                    </td>
                    <td className="py-2 pr-3 text-slate-700">{p.orden_honda_pendiente?.orden_bloqueante || "—"}</td>
                    <td className="py-2">
                      <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                        diasDesde(p.orden_honda_pendiente?.desde) > 15 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                        {diasDesde(p.orden_honda_pendiente?.desde)} día(s)
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <Panel titulo="Lotes" testId="control-honda-lotes-panel" className="lg:col-span-1">
          {cargandoLotes ? (
            <p className="text-sm text-slate-500">Cargando...</p>
          ) : lotes.length === 0 ? (
            <p data-testid="control-honda-lotes-vacio" className="text-sm text-slate-400">Aún no hay lotes de verificación.</p>
          ) : (
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
              {lotes.map((l) => (
                <button key={l.id} data-testid={`control-honda-lote-${l.id}`} onClick={() => setLoteId(l.id)}
                  className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                    loteId === l.id ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <p className="font-medium text-slate-800">{rangoLote(l)}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1.5">
                    {l.total} trabajo(s) · {l.terminado ? "Terminado" : "En proceso"}
                    {l.correo_enviado && <span className="text-amber-600">· correo enviado</span>}
                  </p>
                </button>
              ))}
            </div>
          )}
        </Panel>

        <Panel titulo="Detalle del lote" testId="control-honda-detalle-panel" className="lg:col-span-2">
          {!loteId ? (
            <p className="text-sm text-slate-400">Seleccione un lote de la lista para ver el detalle.</p>
          ) : errorDetalle ? (
            <p data-testid="control-honda-detalle-error" className="text-sm text-red-600">{errorDetalle}</p>
          ) : !detalle ? (
            <p className="text-sm text-slate-500">Cargando...</p>
          ) : !detalle.terminado ? (
            <div data-testid="control-honda-detalle-progreso"
              className="flex items-center justify-center gap-2 text-sm text-slate-600 py-8">
              <Loader2 size={16} className="animate-spin" />
              Consultando Honda… {procesados} de {items.length || "…"}
            </div>
          ) : items.length === 0 ? (
            <p data-testid="control-honda-detalle-sin-items" className="text-sm text-slate-400">
              No hubo cupones finalizados en este rango.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="control-honda-items-tabla" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                    <th className="py-2 pr-3 font-semibold">Trabajo</th>
                    <th className="py-2 pr-3 font-semibold">Vehículo</th>
                    <th className="py-2 pr-3 font-semibold">Orden Nextep</th>
                    <th className="py-2 pr-3 font-semibold">Estado</th>
                    <th className="py-2 pr-3 font-semibold">Motivo</th>
                    <th className="py-2 text-right font-semibold">Sugerida</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id} data-testid={`control-honda-item-${i.id}`} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-medium text-indigo-700">{numTrabajo(i.trabajo_numero)}</td>
                      <td className="py-2 pr-3 text-slate-700">
                        {i.vehiculo ? `${i.vehiculo.modelo} · ${i.vehiculo.placa}` : "—"}
                      </td>
                      <td className="py-2 pr-3 text-slate-700">{i.orden_nextep || "—"}</td>
                      <td className="py-2 pr-3">
                        <span data-testid={`control-honda-item-estado-${i.id}`}
                          className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${ESTADO_CLS[i.estado] || ""}`}>
                          {ESTADO_LABEL[i.estado] || i.estado}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-slate-600 text-xs max-w-xs">{i.motivo || "—"}</td>
                      <td className="py-2 text-right">
                        {i.orden_sugerida ? (
                          <Button data-testid={`control-honda-usar-sugerida-${i.id}`} size="sm" variant="outline"
                            disabled={usandoSugeridaId === i.id} onClick={() => usarSugerida(i)}
                            className="text-xs h-7 gap-1">
                            <Lightbulb size={12} />
                            {usandoSugeridaId === i.id ? "..." : i.orden_sugerida}
                          </Button>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end mt-2">
                <button data-testid="control-honda-refrescar-btn" onClick={cargarDetalle}
                  className="text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-1">
                  <RefreshCw size={12} /> Refrescar
                </button>
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
